import React, { ReactNode } from "react";

import BaseField, { IProps as IBaseFieldProps } from "./BaseField";
import { extractFormValuesFromElement } from "./helpers/formValueExtractionHelpers";

export type IBaseField = BaseField<IBaseFieldProps>;

export type IFormContext = {
	setField: (name: string, field: IBaseField) => void;
	unSetField: (name: string) => void;
	onFieldChange: (name: string, field: IBaseField) => void;
	onFieldFocusChange: (name: string, field: IBaseField, focused: boolean) => void;
	isInputFocused: (name: string) => boolean;
	hasOneFocusedInput: () => boolean;
};

type IFields = {
	[key: string]: IBaseField;
};

type IState = {
	inputFocused: {
		name: string;
		field: IBaseField | null;
		focused: boolean;
	};
};

export type FormValue = string | string[];
export type FormValues = Record<string, FormValue>;

export const getStringValue = (value: FormValue | undefined): string => {
	if (typeof value === "undefined") {
		return "";
	}
	return Array.isArray(value) ? value[0] ?? "" : value;
};

export const mapFormValues = (values: FormValues): Record<string, string> => {
	const normalized: Record<string, string> = {};
	Object.entries(values).forEach(([key, value]) => {
		normalized[key] = getStringValue(value);
	});
	return normalized;
};

type BivariantFormSubmit = {
	bivarianceHack(e: React.FormEvent<HTMLFormElement> | null, values: FormValues): void;
}["bivarianceHack"];

export type IProps = {
	onFieldFocusChange?: (name: string, field: IBaseField, focused: boolean) => void;
	onFieldChange?: (name: string, field: IBaseField) => void;
	onSubmit?: BivariantFormSubmit;
	className?: string;
	children?: ReactNode;
};

const noop = (): undefined => undefined;
const noopBoolean = (): boolean => false;

export const FormContext = React.createContext<IFormContext>({
	setField: () => {
		noop();
	},
	unSetField: () => {
		noop();
	},
	onFieldChange: () => {
		noop();
	},
	onFieldFocusChange: () => {
		noop();
	},
	isInputFocused: () => noopBoolean(),
	hasOneFocusedInput: () => noopBoolean(),
});

export default class Form extends React.Component<IProps, IState> {
	protected fields: IFields = {};
	public formRef: React.RefObject<HTMLFormElement | null>;

	constructor(props: IProps) {
		super(props);

		this.state = {
			inputFocused: {
				name: "",
				field: null,
				focused: false,
			},
		};

		this.setField = this.setField.bind(this);
		this.unSetField = this.unSetField.bind(this);
		this.onFieldChange = this.onFieldChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
		this.formRef = React.createRef<HTMLFormElement>();
		this.onFieldFocusChange = this.onFieldFocusChange.bind(this);
		this.isInputFocused = this.isInputFocused.bind(this);
		this.hasOneFocusedInput = this.hasOneFocusedInput.bind(this);
	}

	public override render(): React.JSX.Element {
		return (
			<FormContext.Provider
				value={{
					setField: this.setField,
					unSetField: this.unSetField,
					onFieldChange: this.onFieldChange,
					onFieldFocusChange: this.onFieldFocusChange,
					isInputFocused: this.isInputFocused,
					hasOneFocusedInput: this.hasOneFocusedInput,
				}}>
				<form className={this.props.className} ref={this.formRef as React.RefObject<HTMLFormElement>} onSubmit={this.onSubmit}>
					{this.props.children}
				</form>
			</FormContext.Provider>
		);
	}

	public async onSubmit(e: React.FormEvent<HTMLFormElement> | null): Promise<{ values: FormValues }> {
		e?.preventDefault();
		const form = (e?.target as HTMLFormElement) ?? this.formRef.current;
		if (!form) {
			// Form element not found - this is expected in some cases
			return { values: {} as FormValues };
		}

		const collectedValues = extractFormValuesFromElement(form);

		this.props.onSubmit?.(e, collectedValues);

		return { values: collectedValues };
	}

	protected setField(name: string, field: IBaseField): void {
		this.fields[name] = field;
	}

	protected unSetField(name: string): void {
		delete this.fields[name];
	}

	protected hasOneFocusedInput(): boolean {
		return this.state.inputFocused.focused;
	}

	protected isInputFocused(name: string): boolean {
		return this.state.inputFocused.name === name && this.state.inputFocused.focused;
	}

	protected onFieldFocusChange(name: string, field: IBaseField, focused: boolean): void {
		this.setState({
			inputFocused: {
				name,
				field,
				focused,
			},
		});

		if (this.props.onFieldFocusChange) {
			this.props.onFieldFocusChange(name, field, focused);
		}
	}

	protected onFieldChange(name: string, field: IBaseField): void {
		this.props.onFieldChange?.(name, field);
	}
}
